import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import clsx from 'clsx';

import { Button } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { SupportConversationStatus, SupportEscalationChannel, SupportMessageRole } from '@/enums';
import {
  useEscalateSupportConversationMutation,
  useGetSupportConversation,
  useRateSupportConversationMutation,
  useSendSupportMessageMutation
} from '@/services/supportChat.service';
import { TestIds } from '@/shared/test-ids';
import { useSupportChatStore } from '@/store/supportChat';
import { ErrorResponse, SupportMessage } from '@/types';

import { ChatBubbleIcon, CloseChatIcon } from './SupportChatIcons';

const MAX_MESSAGE_LENGTH = 2000;

// The API's display messages follow the server culture; the chat shows its own translations.
const ERROR_TRANSLATION_KEYS: Record<string, string> = {
  SupportChatLimitReached: 'supportChatErrorLimit',
  SupportChatUnavailable: 'supportChatErrorUnavailable',
  SupportEscalationFailed: 'supportChatErrorEscalation',
  SupportConversationAlreadyEscalated: 'supportChatErrorAlreadyEscalated'
};

const LOST_CONVERSATION_CODES = ['SupportConversationNotFound', 'InaccessibleSupportConversation'];

const errorCodeOf = (error: ErrorResponse) => error?.response?.data?.ErrorMessages?.[0]?.Code;

export const SupportChat = () => {
  const [t] = useTranslation('common');
  const { pathname } = useLocation();
  const { isOpen, setIsOpen, conversationId, setConversationId } = useSupportChatStore();

  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState(SupportConversationStatus.OPEN);
  const [suggestEscalation, setSuggestEscalation] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [isEscalationFormOpen, setIsEscalationFormOpen] = useState(false);
  const [note, setNote] = useState('');
  const [rating, setRating] = useState<'none' | 'asked' | 'given'>('none');
  const [escalationNotice, setEscalationNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messageListRef = useRef<HTMLDivElement>(null);

  const escalatedText = (channel?: SupportEscalationChannel, ticketNumber?: string) =>
    channel === SupportEscalationChannel.TICKET && ticketNumber
      ? t('supportChatEscalatedTicket', { ticketNumber })
      : t('supportChatEscalatedEmail');

  const resetConversation = () => {
    setConversationId(null);
    setMessages([]);
    setStatus(SupportConversationStatus.OPEN);
    setSuggestEscalation(false);
    setUrgent(false);
    setIsEscalationFormOpen(false);
    setNote('');
    setRating('none');
    setEscalationNotice(null);
    setError(null);
  };

  const showError = (requestError: ErrorResponse) => {
    const code = errorCodeOf(requestError);
    if (code && LOST_CONVERSATION_CODES.includes(code)) {
      resetConversation();
    }
    setError(t((code && ERROR_TRANSLATION_KEYS[code]) || 'supportChatErrorGeneric'));
  };

  const { data: conversationResponse } = useGetSupportConversation(
    isOpen ? conversationId : null,
    resetConversation
  );

  // Restores the conversation after a reload, and replaces the optimistic user message with the
  // stored one once a new conversation has an id.
  useEffect(() => {
    const conversation = conversationResponse?.data;
    if (!conversation) {
      return;
    }

    const lastMessage = conversation.messages[conversation.messages.length - 1];
    setMessages(conversation.messages);
    setStatus(conversation.status);
    setSuggestEscalation(!!lastMessage?.escalationSuggested);
    setUrgent(conversation.urgent);
    // Keep a rating question the last reply just asked; only a stored rating overrides it.
    if (conversation.helpful !== undefined && conversation.helpful !== null) {
      setRating('given');
    }
    setEscalationNotice(
      conversation.status === SupportConversationStatus.ESCALATED
        ? escalatedText(conversation.escalationChannel, conversation.ticketNumber)
        : null
    );
  }, [conversationResponse]);

  useEffect(() => {
    const list = messageListRef.current;
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  }, [messages, isOpen, escalationNotice, error, isEscalationFormOpen]);

  const sendMutation = useSendSupportMessageMutation({
    onSuccess: ({ data }) => {
      setConversationId(data.conversationId);
      setMessages((previous) => [...previous, data.message]);
      setSuggestEscalation(data.suggestEscalation);
      setUrgent(data.urgent);
      setRating(data.suggestEscalation ? 'none' : 'asked');
    },
    onError: showError
  });

  const escalateMutation = useEscalateSupportConversationMutation({
    onSuccess: ({ data }) => {
      setStatus(SupportConversationStatus.ESCALATED);
      setIsEscalationFormOpen(false);
      setNote('');
      setEscalationNotice(escalatedText(data.channel, data.ticketNumber));
    },
    onError: showError
  });

  const rateMutation = useRateSupportConversationMutation();

  const isEscalated = status === SupportConversationStatus.ESCALATED;

  const handleSend = () => {
    const text = draft.trim();
    if (!text || sendMutation.isLoading || isEscalated) {
      return;
    }

    setError(null);
    setRating('none');
    setMessages((previous) => [
      ...previous,
      {
        id: -Date.now(),
        role: SupportMessageRole.USER,
        content: text,
        escalationSuggested: false,
        createdAt: new Date().toISOString()
      }
    ]);
    setDraft('');
    sendMutation.mutate({ conversationId, message: text, pagePath: pathname });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleEscalate = () => {
    if (!conversationId || escalateMutation.isLoading) {
      return;
    }

    setError(null);
    escalateMutation.mutate({
      id: conversationId,
      payload: { note: note.trim(), pagePath: pathname, userAgent: navigator.userAgent }
    });
  };

  const handleRate = (helpful: boolean) => {
    if (!conversationId) {
      return;
    }

    rateMutation.mutate({ id: conversationId, helpful });
    setRating('given');
    if (!helpful) {
      setSuggestEscalation(true);
    }
  };

  const canEscalate = !!conversationId && !isEscalated && !sendMutation.isLoading;

  return (
    <>
      {isOpen && (
        <div
          role='dialog'
          aria-label={t('supportChatTitle')}
          data-clarity-mask='True'
          data-testid={TestIds.supportChat.panel}
          className='fixed inset-x-4 bottom-4 z-[150] flex h-[560px] max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-[10px] border border-accent-light-gray bg-primary-white shadow-primary sm:left-auto sm:w-96 md:bottom-28 md:max-h-[calc(100dvh-9rem)] [@media(min-width:1200px)]:bottom-24'>
          <div className='flex items-center justify-between gap-2 bg-primary-blue px-4 py-3 text-primary-white'>
            <span className='font-bold'>{t('supportChatTitle')}</span>
            <div className='flex items-center gap-3'>
              {(messages.length > 0 || isEscalated) && (
                <button
                  type='button'
                  data-testid={TestIds.supportChat.newConversationButton}
                  className='text-sm underline-offset-2 hover:underline'
                  onClick={resetConversation}>
                  {t('supportChatNewConversation')}
                </button>
              )}
              <button
                type='button'
                aria-label={t('supportChatClose')}
                onClick={() => setIsOpen(false)}>
                <CloseChatIcon className='size-5' />
              </button>
            </div>
          </div>

          <div
            ref={messageListRef}
            aria-live='polite'
            className='flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3 text-sm'>
            <MessageBubble
              author={SupportMessageRole.ASSISTANT}
              content={t('supportChatWelcome')}
            />
            <p className='text-2xs text-secondary-steel-blue'>{t('supportChatDisclaimer')}</p>

            {messages.map((message) => (
              <MessageBubble key={message.id} author={message.role} content={message.content} />
            ))}

            {sendMutation.isLoading && (
              <p className='text-xs italic text-secondary-steel-blue'>{t('supportChatThinking')}</p>
            )}

            {rating === 'asked' && !isEscalated && !sendMutation.isLoading && (
              <div className='flex items-center gap-3 text-xs text-secondary-steel-blue'>
                <span>{t('supportChatHelpful')}</span>
                <button
                  type='button'
                  className='font-bold text-primary-blue'
                  onClick={() => handleRate(true)}>
                  {t('yes')}
                </button>
                <button
                  type='button'
                  className='font-bold text-primary-blue'
                  onClick={() => handleRate(false)}>
                  {t('no')}
                </button>
              </div>
            )}

            {escalationNotice && (
              <p
                data-testid={TestIds.supportChat.escalationNotice}
                className='rounded-[10px] bg-accent-light-blue p-3 text-primary-blue'>
                {escalationNotice}
              </p>
            )}

            {error && (
              <p role='alert' className='rounded-[10px] bg-red-50 p-3 text-red-700'>
                {error}
              </p>
            )}
          </div>

          <div className='border-t border-accent-light-gray p-3'>
            {isEscalated ? (
              <Button
                variant={ButtonVariant.SECONDARY}
                className='w-full'
                handleOnClick={resetConversation}>
                {t('supportChatNewConversation')}
              </Button>
            ) : isEscalationFormOpen ? (
              <div className='flex flex-col gap-2'>
                <p className='text-sm'>{t('supportChatEscalationPrompt')}</p>
                <textarea
                  value={note}
                  maxLength={MAX_MESSAGE_LENGTH}
                  rows={3}
                  placeholder={t('supportChatEscalationNotePlaceholder')}
                  className='w-full resize-none rounded-[10px] border border-accent-light-gray p-2 text-sm'
                  onChange={(event) => setNote(event.target.value)}
                />
                <div className='flex justify-end gap-2'>
                  <Button
                    variant={ButtonVariant.SECONDARY}
                    handleOnClick={() => setIsEscalationFormOpen(false)}>
                    {t('cancel')}
                  </Button>
                  <Button
                    dataTestId={TestIds.supportChat.escalateSubmitButton}
                    disabled={escalateMutation.isLoading}
                    handleOnClick={handleEscalate}>
                    {t('supportChatEscalationSend')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className='flex flex-col gap-2'>
                {canEscalate && (
                  <div
                    className={clsx(
                      'flex items-center justify-between gap-2',
                      suggestEscalation && 'rounded-[10px] bg-accent-light-blue p-2'
                    )}>
                    {suggestEscalation && (
                      <span className='text-xs text-primary-blue'>
                        {urgent ? t('supportChatUrgentHint') : t('supportChatEscalationSuggested')}
                      </span>
                    )}
                    <button
                      type='button'
                      data-testid={TestIds.supportChat.talkToPersonButton}
                      className={clsx(
                        'ml-auto shrink-0 text-sm font-bold text-primary-blue',
                        suggestEscalation
                          ? 'rounded-[10px] bg-primary-blue px-3 py-1 text-primary-white'
                          : 'underline-offset-2 hover:underline'
                      )}
                      onClick={() => setIsEscalationFormOpen(true)}>
                      {t('supportChatTalkToPerson')}
                    </button>
                  </div>
                )}
                <div className='flex items-end gap-2'>
                  <textarea
                    value={draft}
                    maxLength={MAX_MESSAGE_LENGTH}
                    rows={2}
                    placeholder={t('supportChatPlaceholder')}
                    data-testid={TestIds.supportChat.input}
                    className='flex-1 resize-none rounded-[10px] border border-accent-light-gray p-2 text-sm'
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  <Button
                    dataTestId={TestIds.supportChat.sendButton}
                    disabled={!draft.trim() || sendMutation.isLoading}
                    handleOnClick={handleSend}>
                    {t('send')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden on phones, where it covers page buttons; their menus offer "Help" instead. */}
      <button
        type='button'
        aria-label={isOpen ? t('supportChatClose') : t('supportChatOpen')}
        aria-expanded={isOpen}
        data-testid={TestIds.supportChat.launcher}
        className='fixed bottom-12 right-4 z-[150] hidden size-14 items-center justify-center rounded-full bg-primary-blue text-primary-white shadow-primary transition-transform hover:scale-105 md:flex [@media(min-width:1200px)]:bottom-5 [@media(min-width:1200px)]:right-5'
        onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <CloseChatIcon className='size-6' /> : <ChatBubbleIcon className='size-7' />}
      </button>
    </>
  );
};

const MessageBubble = ({ author, content }: { author: SupportMessageRole; content: string }) => (
  <div
    data-testid={TestIds.supportChat.message}
    className={clsx(
      'max-w-[85%] whitespace-pre-wrap break-words rounded-[10px] px-3 py-2',
      author === SupportMessageRole.USER
        ? 'self-end bg-primary-blue text-primary-white'
        : 'self-start bg-primary-gray text-primary-blue'
    )}>
    {content}
  </div>
);
